import { TextButton } from '@creditornot/cb-button';
import { colors, typographyCss } from '@creditornot/cb-ingredients';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { bulkChangeCategory } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, Menu } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';
import useItemCategoryChange from 'modules/menu/MenuNavigation/useItemCategoryChange';
import { reportBulkAction } from 'modules/menu/behavioral';
import { buildBulkActionBasePayload } from 'modules/menu/bulk-actions/utils';
import CategoryChangeErrorToast from 'modules/menu/categories/components/CategoryChangeErrorToast';
import CategoryChangeSuccessToast from 'modules/menu/categories/components/CategoryChangeSuccessToast';

import useMenuData from '../useMenuData';
import { flattenCategories, getParentCategory } from '../utils';

type SuccessToastProps = {
  onUndo?: () => void;
  categoryName?: string;
  link: string;
};

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const CategoryLink = styled(Link)`
  ${typographyCss.Body3};
  font-weight: 500;
  margin-block-start: 8px;
  margin-inline-end: 16px;
  color: ${colors.wolt100};

  &:hover {
    color: ${colors.wolt92};
  }
`;

const UndoButton = styled(TextButton).attrs({ size: 'small', variant: 'grey' })`
  margin-block-start: 8px;
`;

const SUCCESS_TIMEOUT = 10 * 1000;

const SuccessToast = ({ onUndo, categoryName, link }: SuccessToastProps) => {
  return (
    <div>
      <FormattedMessage id="menu.bulkActions.categoryChange.successDescription" />
      <Actions>
        {categoryName && (
          <CategoryLink to={link}>
            <FormattedMessage id="menu.goToCategory" values={{ category: categoryName }} />
          </CategoryLink>
        )}
        {onUndo && (
          <UndoButton onClick={onUndo}>
            <FormattedMessage id="state.undo" />
          </UndoButton>
        )}
      </Actions>
    </div>
  );
};

const buildCategoryLink = (baseUrl: string, categoryId: string, categories: Category[]) => {
  const category = flattenCategories(categories).find(category => category.id === categoryId);
  if (!category) {
    return baseUrl;
  }
  const parentCategory = getParentCategory(category.id, categories);
  if (parentCategory) {
    return `${baseUrl}/${parentCategory.id}/subcategories/${category.id}`;
  }
  return `${baseUrl}/${category.id}`;
};

const useBulkItemCategoryChange = (menu: Menu, url: string) => {
  const { updateMenuData } = useMenuData();

  const changeItemCategory = useCallback(
    async ({
      itemIds,
      destinationCategoryId,
      dataToRevert,
    }: {
      itemIds: Set<string>;
      destinationCategoryId: string;
      dataToRevert?: Record<string, string[]>;
    }) => {
      try {
        const updatedMenu = await bulkChangeCategory(menu.id, {
          ...buildBulkActionBasePayload(menu.categories, itemIds),
          destination_category_id: {
            $oid: destinationCategoryId,
          },
        });

        const categoryName = flattenCategories(updatedMenu.categories).find(
          category => category.id === destinationCategoryId,
        )?.name;

        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });

        const link = buildCategoryLink(url, destinationCategoryId, updatedMenu.categories);

        if (!dataToRevert) {
          toast.success(
            <SuccessToast link={link} categoryName={categoryName && localize(categoryName, menu.primary_language)} />,
          );
          return;
        }

        const undo = async (menu: Menu, categoryIds: string[] = Object.keys(dataToRevert)): Promise<Menu> => {
          const [firstCategoryId, ...otherCategoryIds] = categoryIds;

          const revertedMenu = await bulkChangeCategory(menu.id, {
            ...buildBulkActionBasePayload(menu.categories, new Set(dataToRevert[firstCategoryId])),
            destination_category_id: {
              $oid: firstCategoryId,
            },
          });

          if (otherCategoryIds.length === 0) {
            return Promise.resolve(revertedMenu);
          }

          return await undo(revertedMenu, otherCategoryIds);
        };

        toast.success(
          <SuccessToast
            link={link}
            categoryName={categoryName && localize(categoryName, menu.primary_language)}
            onUndo={async () => {
              const undoPromise = async () => {
                const revertedMenu = await undo(updatedMenu);
                updateMenuData({ actionType: 'updateMenu', payload: revertedMenu });
              };
              try {
                toast.dismiss();
                toast.promise(undoPromise(), { loading: '', success: '', error: error => error.message });
              } catch (error) {
                toast.error(<FormattedMessage id="menu.bulkActions.categoryChange.errorDescription" />, {
                  duration: Infinity,
                });
              }
            }}
          />,
          {
            duration: SUCCESS_TIMEOUT,
          },
        );
      } catch {
        toast.error(<FormattedMessage id="menu.bulkActions.categoryChange.errorDescription" />, {
          duration: Infinity,
        });
      }
    },
    [updateMenuData, menu.categories, menu.id, menu.primary_language, url],
  );

  return useMutation(changeItemCategory).mutateAsync;
};

export const useMoveMenuItems = (menuEditorRootUrl: string) => {
  const { menu, categories, items } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const intl = useIntl();
  const bulkItemsCategory = useBulkItemCategoryChange(menu, menuEditorRootUrl);
  const changeItemCategory = useItemCategoryChange();

  const categoryItemBindingIdsRecord = useMemo(
    () =>
      flattenCategories(categories).reduce(
        (obj, current) => ({
          [current.id]: current.item_bindings.map(({ item_id }) => item_id),
          ...obj,
        }),
        {} as Record<string, string[]>,
      ),
    [categories],
  );

  const handleBulkMoveItemsToCategory = useCallback(
    async (overId: string, selectedIds: Set<string>, dataToRevert?: Record<string, string[]>) => {
      reportBulkAction(selectedIds.size, 'BULK_CHANGE_ITEM_CATEGORY', { categoryChangeMethod: 'dragAndDrop' });
      const ids = Array.from(selectedIds.values());
      const isDraggingToSameCategory = ids.every(id => categoryItemBindingIdsRecord[overId].includes(id));
      if (isDraggingToSameCategory) {
        return;
      }

      return await bulkItemsCategory({
        itemIds: selectedIds,
        destinationCategoryId: overId,
        dataToRevert,
      });
    },
    [categoryItemBindingIdsRecord, bulkItemsCategory],
  );

  const handleMoveItemToCategory = useCallback(
    async (activeId: string, overId: null | string) => {
      const item = items.find(item => item.id === activeId);
      const itemName = item ? localize(item.name, menu.primary_language) : activeId;
      const menuCategoryName = flattenCategories(categories).find(category => category.id === overId)?.name;

      const categoryName = !overId
        ? intl.formatMessage({
            id: 'menu.nonCategorised',
          })
        : menuCategoryName
          ? localize(menuCategoryName, menu.primary_language)
          : '';
      const link = !overId
        ? `${menuEditorRootUrl}/no-category`
        : buildCategoryLink(menuEditorRootUrl, overId, categories);

      try {
        const undoItemCategoryChange = await changeItemCategory(overId, activeId);
        toast.success(
          <CategoryChangeSuccessToast
            categoryName={categoryName}
            itemName={itemName}
            link={link}
            onUndo={() => {
              toast.dismiss();
              toast.promise(undoItemCategoryChange(), { loading: '', success: '', error: error => error.message });
            }}
          />,
          {
            duration: SUCCESS_TIMEOUT,
          },
        );
      } catch (error) {
        toast.error(<CategoryChangeErrorToast itemName={itemName} categoryName={categoryName} />, {
          duration: Infinity,
        });
      }
    },
    [changeItemCategory, intl, menu, items, categories, menuEditorRootUrl],
  );

  const moveItemsToCategory = useCallback(
    async (activeId: string, overId: string, selectedIds?: Set<string>) => {
      if (!selectedIds) {
        return await handleMoveItemToCategory(activeId, overId);
      }

      const dataToRevert = Array.from(selectedIds.values()).reduce(
        (obj, value) => {
          const categoryId = Object.keys(categoryItemBindingIdsRecord).find(id =>
            categoryItemBindingIdsRecord[id].includes(value),
          );
          return !categoryId
            ? obj
            : {
                ...obj,
                [categoryId]: (obj[categoryId] ?? []).concat(value),
              };
        },
        {} as Record<string, string[]>,
      );

      return await handleBulkMoveItemsToCategory(overId, selectedIds, dataToRevert);
    },
    [categoryItemBindingIdsRecord, handleBulkMoveItemsToCategory, handleMoveItemToCategory],
  );

  const assignNoCategoryItemsToCategory = useCallback(
    async (activeId: string, overId: string, selectedIds?: Set<string>) => {
      if (!selectedIds) {
        return await handleMoveItemToCategory(activeId, overId);
      }
      return await handleBulkMoveItemsToCategory(overId, selectedIds);
    },
    [handleBulkMoveItemsToCategory, handleMoveItemToCategory],
  );

  const moveItemToNoCategory = useCallback(
    async (activeId: string) => {
      return await handleMoveItemToCategory(activeId, null);
    },
    [handleMoveItemToCategory],
  );

  return {
    moveItemsToCategory,
    assignNoCategoryItemsToCategory,
    moveItemToNoCategory,
  };
};
